import * as React from 'react'
import { cn } from '../utils'
import { semanticTokens } from '@supabank/config/theme'
import { Package, type LucideIcon } from 'lucide-react'

export interface EmptyStateProps extends React.HTMLAttributes<HTMLDivElement> {
  icon?: LucideIcon
  title: string
  description: string
  action?: {
    label: string
    onClick: () => void
  }
}

/**
 * EmptyState - Empty state display with polished styling
 * 
 * Copy template: "No {entity} yet. {Action} to discover {entity}."
 * 
 * Design Canon:
 * - Icon: 48px, muted foreground
 * - Title: h3, centered
 * - Description: body text, muted, max-width for readability
 * - Action: Primary button with proper focus states
 * - Motion: fade-in animation, respects reduced motion
 * 
 * @example
 * <EmptyState
 *   icon={Package}
 *   title="No customers yet"
 *   description="Create your first customer to get started"
 *   action={{ label: "Create Customer", onClick: () => {} }}
 * />
 */
export function EmptyState({ 
  icon: Icon = Package,
  title, 
  description, 
  action,
  className, 
  ...props 
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center py-16 px-4 text-center',
        // Subtle background for visual hierarchy
        'rounded-xl bg-muted/30',
        semanticTokens.motion.fadeIn,
        semanticTokens.motion.reduced,
        className
      )}
      role="status"
      aria-label={title}
      {...props}
    >
      {/* Icon with subtle animation - Safe render to prevent React error #130 */}
      {Icon && (
        <div className={cn(
          'mb-6 p-4 rounded-full',
          'bg-muted/50',
          semanticTokens.fg.muted
        )}>
          <Icon className="h-10 w-10" aria-hidden="true" />
        </div>
      )}
      
      {/* Title */}
      <h3 className={cn(
        'mb-3', 
        semanticTokens.typography.h3, 
        semanticTokens.fg.default
      )}>
        {title}
      </h3>
      
      {/* Description */}
      <p className={cn(
        'mb-8 max-w-sm leading-relaxed', 
        semanticTokens.typography.body, 
        semanticTokens.fg.muted
      )}>
        {description}
      </p>
      
      {/* Action Button */}
      {action && (
        <button
          onClick={action.onClick}
          className={cn(
            'px-6 py-3 text-sm font-medium',
            // Min touch target
            'min-h-[44px]',
            // Styling
            semanticTokens.button.primary,
            semanticTokens.radius.lg,
            // Focus ring
            semanticTokens.focus.ring,
            // Transitions
            semanticTokens.motion.standard,
            // Hover & active states
            'hover:opacity-90 hover:shadow-md',
            'active:scale-[0.98] transition-transform',
            semanticTokens.motion.reduced
          )}
        >
          {action.label}
        </button>
      )}
    </div>
  )
}

